import React, { LegacyRef } from 'react';
import { Flex, Icon, Text } from '@highereducation/hen-ui';
import { parseHtmlString } from '@de/shared/utils';
import { getDcsValues } from '@de/utils/dataFromFlowUtils';
import useViewEvents from '@de/hooks/useViewEvents';
import type { FlowState } from '@de/providers/dynamicExperienceReducer';
import type { EventContext } from '@de/providers/DynamicExperienceProvider';
import { InfoBannerIcon, type InfoBannerProps } from '@de/types/types';
import type { LucideIconName } from '@highereducation/hen-ui';

export interface InfoBannerComponentProps extends InfoBannerProps {
  flowState?: FlowState;
  eventContext?: EventContext;
}

const ICON_MAP: Record<InfoBannerIcon, LucideIconName> = {
  [InfoBannerIcon.INFO]: 'info',
  [InfoBannerIcon.WARNING]: 'triangle-alert',
};

const STYLE_MAP: Record<InfoBannerIcon, { container: string; icon: string }> = {
  [InfoBannerIcon.INFO]: {
    container: 'border-info-500 bg-info-100',
    icon: 'text-info-500',
  },
  [InfoBannerIcon.WARNING]: {
    container: 'border-warning-500 bg-warning-100',
    icon: 'text-warning-500',
  },
};

const resolveDcsPlaceholders = (text: string, flowState: FlowState): string => {
  if (!text || !flowState) return text;

  const { degree, subject, category, career } = getDcsValues(flowState);

  return text
    .replace(/\{\{degree\}\}/gi, degree)
    .replace(/\{\{subject\}\}/gi, subject)
    .replace(/\{\{category\}\}/gi, category)
    .replace(/\{\{career\}\}/gi, career);
};

export const InfoBanner: React.FC<InfoBannerComponentProps> = ({
  title,
  message,
  icon,
  flowState,
  eventContext,
}) => {
  const resolvedMessage = flowState
    ? resolveDcsPlaceholders(message, flowState)
    : message;

  const { ref: visibilityRef } = useViewEvents(
    eventContext
      ? [
          {
            name: 'ElementViewed',
            payload: {
              correlationId: eventContext.elementLoadedCorrelationId,
              webElement: {
                elementType: 'dexp-flow-banner',
                htmlId: 'info-banner',
                name: title || 'info-banner',
                location: eventContext.placement || '',
                text: resolvedMessage || '',
              },
            },
          },
        ]
      : [],
  );

  const iconName = ICON_MAP[icon] ?? ICON_MAP[InfoBannerIcon.INFO];
  const styles = STYLE_MAP[icon] ?? STYLE_MAP[InfoBannerIcon.INFO];

  return (
    <Flex
      ref={visibilityRef as LegacyRef<HTMLDivElement>}
      className={`w-full rounded-md border px-4 py-3 gap-3 mb-6 ${styles.container}`}
      align='center'
    >
      <div className={`shrink-0 w-5 h-5 ${styles.icon}`}>
        <Icon icon={iconName} size='sm' aria-hidden />
      </div>
      <div className='flex flex-col gap-1 text-left'>
        {title && (
          <Text variant='l1' color='heading' weight='semibold'>
            {title}
          </Text>
        )}
        {resolvedMessage && (
          <Text variant='l1' color='subtext'>
            {parseHtmlString(resolvedMessage)}
          </Text>
        )}
      </div>
    </Flex>
  );
};
